import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaPlus, FaEdit, FaSave, FaTimes, FaHome, FaMapMarkerAlt, FaBed, FaUsers, FaMoneyBillWave, FaImage, FaList, FaClipboardList, FaTag, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import BasicInfoTab from './form-tabs/BasicInfoTab';
import ImagesTab from './form-tabs/ImagesTab';
import FeaturesTab from './form-tabs/FeaturesTab';
import PoliciesTab from './form-tabs/PoliciesTab';
import styled from 'styled-components';

const FormCard = styled(motion.div)`
  background: ${props => props.darkMode ? '#1e1e1e' : 'white'};
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, ${props => props.darkMode ? '0.3' : '0.05'});
`;

const TabsContainer = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 1.5rem;
  
  @media (max-width: 576px) {
    display: none;
  }
`;

const TabButton = styled.button`
  padding: 10px 15px;
  background: ${props => props.active ? '#3498db' : 'transparent'};
  color: ${props => props.active ? 'white' : '#4a5568'};
  border: none;
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 8px;
  &:hover {
    background: ${props => props.active ? '#3498db' : '#f1f5f9'};
  }
`;

const MobileTabsContainer = styled.div`
  display: none;
  align-items: center;
  justify-content: space-between;
  background: ${props => props.darkMode ? '#2d2d2d' : '#f8fafc'};
  padding: 12px;
  border-radius: 8px;
  margin-bottom: 1.5rem;
  
  @media (max-width: 576px) {
    display: flex;
  }
`;

const MobileTabName = styled.div`
  font-weight: 600;
  text-align: center;
  flex: 1;
  color: ${props => props.darkMode ? '#f5f5f5' : 'inherit'};
`;

const TabNavigationButton = styled.button`
  background: transparent;
  border: none;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  cursor: pointer;
  color: #3498db;
  transition: all 0.2s;
  
  &:hover {
    background: rgba(52, 152, 219, 0.1);
  }
  
  &:disabled {
    color: #cbd5e0;
    cursor: not-allowed;
    
    &:hover {
      background: transparent;
    }
  }
`;

const PropertyForm = ({ 
  isEditing, 
  formData, 
  handleSubmit, 
  handleChange, 
  handleCancelEdit,
  tempArrayFields,
  setTempArrayFields,
  handleArrayItemChange,
  handleAddArrayItem,
  handleRemoveArrayItem,
  handleDragEnd,
  darkMode
}) => {
  const [activeTab, setActiveTab] = useState('basic');
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Define tabs array for easier navigation
  const tabs = [
    { id: 'basic', label: 'Basic Info', icon: <FaHome size={14} /> },
    { id: 'images', label: 'Images & Documents', icon: <FaImage size={14} /> },
    { id: 'features', label: 'Features & Amenities', icon: <FaTag size={14} /> },
    { id: 'policies', label: 'Policies & Rules', icon: <FaClipboardList size={14} /> }
  ];
  
  // Get current tab index
  const currentTabIndex = tabs.findIndex(tab => tab.id === activeTab);
  
  // Handle form submission with loading state
  const handleFormSubmit = async (e) => {
    setIsSubmitting(true);
    try {
      await handleSubmit(e);
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Prevent form submission with Enter key
  const handleKeyDown = (e) => {
    // If Enter key is pressed, prevent submission
    if (e.key === 'Enter') {
      e.preventDefault();
      
      // Allow default behavior in textareas only
      if (e.target.tagName === 'TEXTAREA') {
        return;
      }
      
      // If not on the last tab, pressing Enter will go to next tab
      if (activeTab !== 'policies') {
        goToNextTab();
      }
    }
  };
  
  // Go to next tab
  const goToNextTab = () => {
    if (currentTabIndex < tabs.length - 1) {
      setActiveTab(tabs[currentTabIndex + 1].id);
      // Scroll to top on mobile
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };
  
  // Go to previous tab
  const goToPrevTab = () => {
    if (currentTabIndex > 0) {
      setActiveTab(tabs[currentTabIndex - 1].id);
      // Scroll to top on mobile
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };
  
  const renderTabContent = () => {
    switch(activeTab) {
      case 'basic':
        return (
          <BasicInfoTab 
            formData={formData} 
            handleChange={handleChange}
            darkMode={darkMode} 
          />
        );
      case 'images':
        return (
          <ImagesTab 
            formData={formData} 
            handleChange={handleChange}
            tempArrayFields={tempArrayFields}
            setTempArrayFields={setTempArrayFields}
            handleArrayItemChange={handleArrayItemChange}
            handleAddArrayItem={handleAddArrayItem}
            handleRemoveArrayItem={handleRemoveArrayItem}
            handleDragEnd={handleDragEnd}
            darkMode={darkMode}
          />
        );
      case 'features':
        return (
          <FeaturesTab 
            formData={formData}
            handleChange={handleChange}
            tempArrayFields={tempArrayFields}
            handleArrayItemChange={handleArrayItemChange}
            handleAddArrayItem={handleAddArrayItem}
            handleRemoveArrayItem={handleRemoveArrayItem}
            handleDragEnd={handleDragEnd}
            darkMode={darkMode}
          />
        );
      case 'policies':
        return (
          <PoliciesTab 
            formData={formData}
            handleChange={handleChange}
            tempArrayFields={tempArrayFields}
            handleArrayItemChange={handleArrayItemChange}
            handleAddArrayItem={handleAddArrayItem}
            handleRemoveArrayItem={handleRemoveArrayItem}
            handleDragEnd={handleDragEnd}
            darkMode={darkMode}
          />
        );
      default:
        return null;
    }
  };

  return (
    <FormCard
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: 'auto' }}
      exit={{ opacity: 0, height: 0 }}
      transition={{ duration: 0.3 }}
      className="mb-4"
      darkMode={darkMode}
    >
      <div className={`p-3 ${isEditing ? 'bg-warning' : 'bg-primary text-white'}`}>
        <h3 className="mb-0 d-flex align-items-center gap-2">
          {isEditing ? (
            <>
              <FaEdit size={18} /> Edit Property
            </>
          ) : (
            <>
              <FaPlus size={18} /> Add New Property
            </>
          )}
        </h3>
      </div>
      <div className="p-4">
        <form onSubmit={handleFormSubmit} onKeyDown={handleKeyDown}>
          {/* Desktop tabs */}
          <TabsContainer>
            {tabs.map(tab => (
              <TabButton 
                key={tab.id}
                type="button" 
                active={activeTab === tab.id}
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.icon} {tab.label}
              </TabButton>
            ))}
          </TabsContainer>
          
          {/* Mobile tabs navigation */}
          <MobileTabsContainer darkMode={darkMode}>
            <TabNavigationButton 
              type="button"
              onClick={goToPrevTab}
              disabled={currentTabIndex === 0}
            >
              <FaChevronLeft />
            </TabNavigationButton>
            
            <MobileTabName darkMode={darkMode}>
              {tabs[currentTabIndex].icon} {tabs[currentTabIndex].label}
            </MobileTabName>
            
            <TabNavigationButton 
              type="button"
              onClick={goToNextTab}
              disabled={currentTabIndex === tabs.length - 1}
            >
              <FaChevronRight />
            </TabNavigationButton>
          </MobileTabsContainer>
          
          <AnimatePresence mode="wait">
            {renderTabContent()}
          </AnimatePresence>
          
          <div className="mt-4 d-flex justify-content-between flex-wrap">
            <div className="mb-2">
              {activeTab !== 'basic' && (
                <button 
                  type="button" 
                  className="btn btn-outline-secondary me-2"
                  onClick={goToPrevTab}
                >
                  <FaChevronLeft className="me-1" /> Previous
                </button>
              )}
            </div>
            
            <div className="mb-2 ms-auto">
              {activeTab !== 'policies' && (
                <button 
                  type="button" 
                  className="btn btn-outline-primary"
                  onClick={goToNextTab}
                >
                  Next <FaChevronRight className="ms-1" />
                </button>
              )}
            </div>
            
            {activeTab === 'policies' && (
              <div className="d-flex gap-2 mt-2 w-100 justify-content-end">
                {isEditing ? (
                  <>
                    <motion.button 
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      type="button" 
                      className="btn btn-outline-secondary"
                      onClick={handleCancelEdit}
                    >
                      <FaTimes className="me-2" /> Cancel
                    </motion.button>
                    <motion.button 
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      type="submit" 
                      className="btn btn-success"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          Saving...
                        </>
                      ) : (
                        <>
                          <FaSave className="me-2" /> Save Changes
                        </>
                      )}
                    </motion.button>
                  </>
                ) : (
                  <motion.button 
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    type="submit" 
                    className="btn btn-primary px-4"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Adding...
                      </>
                    ) : (
                      <>
                        <FaPlus className="me-2" /> Add Property
                      </>
                    )}
                  </motion.button>
                )}
              </div>
            )}
          </div>
        </form>
      </div>
    </FormCard>
  );
};

export default PropertyForm;
